/**
 * "Modo celular": dejar SmartDx como app en el teléfono (PWA) sin App Store.
 *
 * El flujo real que hace un médico nuevo es: abre el link que le llegó por DM o
 * correo → cae en el navegador del celular → si se queda ahí, cada vez tiene que
 * volver a buscar la URL y pelear con las barras del navegador. La app YA es
 * instalable (`public/manifest.json` con `display: standalone`), pero iOS no
 * ofrece nada solo: el "Agregar a pantalla de inicio" está escondido en el menú
 * Compartir de Safari. Este helper es la lógica pura que decide a quién hay que
 * explicárselo y qué pasos mostrarle; la pantalla vive en
 * `components/pwa/InstallAppGuide.jsx`.
 *
 * Todo lo que decide es PURO y toma el user-agent por parámetro para poder
 * testearlo sin navegador (ver `pwa-install.test.js`).
 */

// Marca "ya lo vi, no me lo muestres más". Es GLOBAL del dispositivo a propósito
// (a diferencia de `turno-tutorial-seen.js`, que es por usuario): instalar el
// ícono es una acción del APARATO, no de la cuenta. Si el médico ya lo agregó en
// su iPhone, no tiene por qué volver a verlo al entrar con otra cuenta.
export const INSTALL_GUIDE_KEY = "pwa_install_guide_v1";

// Postergación blanda ("ahora no"): dura lo que dure la pestaña.
const SNOOZE_KEY = "pwa_install_guide_snoozed";

// Evento para abrir la guía a mano desde cualquier parte (chip del menú mobile).
// Se usa un CustomEvent en vez de un context para no tener que envolver el árbol
// entero por una pantalla que aparece una vez.
export const INSTALL_GUIDE_EVENT = "smartdx:open-install-guide";

/** Abre la guía de instalación desde donde sea (navbar, ayuda, etc.). */
export const openInstallGuide = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(INSTALL_GUIDE_EVENT));
};

/**
 * ¿La app ya está corriendo como app instalada (sin barras del navegador)?
 * `display-mode: standalone` cubre Android/Chrome y el iOS moderno;
 * `navigator.standalone` es el legado de iOS que igual conviene mirar.
 */
export const isStandalone = () => {
  if (typeof window === "undefined") return false;
  try {
    if (typeof window.matchMedia === "function") {
      if (window.matchMedia("(display-mode: standalone)").matches) return true;
      // Algunos Android instalados reportan fullscreen/minimal-ui.
      if (window.matchMedia("(display-mode: fullscreen)").matches) return true;
    }
  } catch {
    /* matchMedia puede no existir en entornos de test */
  }
  return window.navigator?.standalone === true;
};

/**
 * Clasifica el navegador para saber QUÉ pasos mostrar.
 *
 * Devuelve `{ os, browser }`:
 *  - os: "ios" | "android" | "other"
 *  - browser: "safari" | "chrome" | "inapp" | "other"
 *
 * El caso "inapp" no es un detalle: los leads llegan por DM de Instagram y el
 * link se abre DENTRO del navegador de Instagram, donde "Agregar a inicio"
 * simplemente no existe. Si no se detecta, el médico sigue los pasos, no
 * encuentra la opción y concluye que la app no se puede instalar.
 */
export const detectPlatform = (ua = "", { maxTouchPoints = 0, platform = "" } = {}) => {
  const s = String(ua);

  // iPadOS 13+ miente y dice "Macintosh": se delata por el touch.
  const isIpadDesktopUA = /Macintosh/i.test(s) && maxTouchPoints > 1;
  const isIOS = /iPad|iPhone|iPod/i.test(s) || /iPad|iPhone/i.test(platform) || isIpadDesktopUA;
  const isAndroid = /Android/i.test(s);

  // Navegadores embebidos dentro de otra app (redes sociales, mail, etc.).
  const isInApp =
    /FBAN|FBAV|FB_IAB|Instagram|Line\/|Twitter|TikTok|MicroMessenger|WhatsApp|LinkedInApp|Snapchat|Pinterest|GSA\//i.test(
      s,
    );

  const os = isIOS ? "ios" : isAndroid ? "android" : "other";

  if (isInApp) return { os, browser: "inapp" };

  if (isIOS) {
    // En iPhone, "Agregar a pantalla de inicio" vive en el menú Compartir y solo
    // Safari lo ofrece de forma confiable. Chrome/Firefox/Edge de iOS son Safari
    // por dentro pero con otra barra: se los manda a Safari.
    const isOtherIosBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|Brave/i.test(s);
    return { os, browser: isOtherIosBrowser ? "other" : "safari" };
  }

  if (/Chrome|Chromium|CriOS/i.test(s) && !/Edg\//i.test(s)) return { os, browser: "chrome" };
  return { os, browser: "other" };
};

/** ¿Se le ofrece la guía sola a este visitante? */
export const shouldOfferInstall = ({
  os,
  standalone = false,
  dismissed = false,
  snoozed = false,
} = {}) => {
  if (standalone) return false; // ya la instaló
  if (dismissed || snoozed) return false;
  return os === "ios" || os === "android"; // en desktop no aplica
};

export const isInstallGuideDismissed = () => {
  try {
    return localStorage.getItem(INSTALL_GUIDE_KEY) === "1";
  } catch {
    // Sin localStorage (modo privado) preferimos NO mostrarlo solo: acá el costo
    // de repetirlo en cada carga es peor que el de no ofrecerlo — el chip
    // "Instalar app" del menú lo deja siempre a mano.
    return true;
  }
};

export const markInstallGuideDismissed = () => {
  try {
    localStorage.setItem(INSTALL_GUIDE_KEY, "1");
  } catch {
    /* noop */
  }
};

export const isInstallGuideSnoozed = () => {
  try {
    return sessionStorage.getItem(SNOOZE_KEY) === "1";
  } catch {
    return false;
  }
};

export const snoozeInstallGuide = () => {
  try {
    sessionStorage.setItem(SNOOZE_KEY, "1");
  } catch {
    /* noop */
  }
};

/**
 * Los pasos, en el orden en que el médico los va a ejecutar. Copy corto porque
 * se lee en un celular con una mano.
 */
export const installSteps = ({ os, browser }) => {
  if (os === "other" && browser !== "inapp") {
    return {
      variant: "desktop",
      title: "Esto se instala desde el celular",
      intro:
        "Estás en un computador. Abre app.smardx.com en tu teléfono y ahí te aparecen los pasos para dejarla como app.",
      steps: [
        { icon: "open", text: "Abre app.smardx.com en el navegador del celular." },
        { icon: "share", text: "Sigue los 3 pasos que te muestra la app para agregarla al inicio." },
        { icon: "check", text: "Inicia sesión con la misma cuenta: ves lo mismo que acá." },
      ],
    };
  }

  if (browser === "inapp") {
    return {
      variant: "inapp",
      title: "Abre el link en tu navegador",
      intro:
        "Estás viendo SmartDx dentro del navegador de otra app (Instagram, WhatsApp…), y ahí no se puede dejar el ícono en el inicio.",
      steps: [
        { icon: "more", text: "Toca el menú (⋯ o ⋮) arriba a la derecha." },
        {
          icon: "open",
          text:
            os === "ios"
              ? 'Elige "Abrir en Safari".'
              : 'Elige "Abrir en Chrome" o "Abrir en el navegador".',
        },
        { icon: "home", text: "Cuando estés en el navegador, vuelve a abrir esta guía y sigue los 3 pasos." },
      ],
    };
  }

  if (os === "ios" && browser !== "safari") {
    return {
      variant: "ios-otro-navegador",
      title: "En iPhone se agrega desde Safari",
      intro:
        "Este navegador no ofrece agregar la app al inicio. Copia el link, ábrelo en Safari y sigue los 3 pasos.",
      steps: [
        { icon: "copy", text: "Copia el link con el botón de abajo." },
        { icon: "open", text: "Abre Safari y pega el link." },
        { icon: "share", text: 'En Safari: Compartir (o ⋯ → Compartir) → "Agregar a pantalla de inicio".' },
      ],
    };
  }

  if (os === "android") {
    return {
      variant: "android",
      title: "Deja SmartDx como app",
      intro: "Se instala desde el navegador, en 20 segundos. No pasa por Play Store.",
      steps: [
        { icon: "more", text: "Toca el menú ⋮ arriba a la derecha del navegador." },
        { icon: "home", text: 'Elige "Instalar aplicación" o "Agregar a pantalla principal".' },
        { icon: "check", text: "Confirma. Te queda el ícono de SmartDx junto a tus otras apps." },
      ],
    };
  }

  return {
    variant: "ios",
    title: "Deja SmartDx como app",
    intro: "Se instala desde Safari, en 20 segundos. No pasa por la App Store.",
    steps: [
      { icon: "share", text: "Toca Compartir: el cuadrito con la flecha ↑. Si no lo ves en la barra de Safari, toca ⋯ y ahí aparece." },
      { icon: "home", text: 'Baja en la lista y elige "Agregar a pantalla de inicio".' },
      { icon: "check", text: 'Toca "Agregar". Te queda el ícono de SmartDx en el inicio.' },
    ],
  };
};
